import React from 'react';
import { 
  Package, CheckCircle
} from 'lucide-react';

/**
 * RentalAddonsSection Component
 * Handles rendering of rental add-ons with per-day/flat pricing
 * Features:
 * - All add-ons in one 2-column grid (category shown on each card)
 * - Per-day or flat pricing methods
 * - Cost calculation based on rental days
 * - Visual checkbox selection
 * - Change tracking in edit mode
 * Extracted from SimplifiedReservationForm for better code organization
 */
const RentalAddonsSection = ({
  reservationSettings,
  selectedAddons,
  setSelectedAddons,
  addonCosts,
  calculationDetails,
  editIndex,
  addonChanges,
  setAddonChanges,
  originalAddons
}) => {
  
  // Get enabled addons from settings
  const getEnabledAddons = () => {
    return (reservationSettings.rentalAddons || []).filter(addon => addon.enabled);
  };

  // Handle addon selection with change tracking
  const handleAddonChange = (addonId, checked) => {
    setSelectedAddons(prev => {
      let newAddons;
      if (checked) {
        newAddons = [...prev, addonId];
      } else {
        newAddons = prev.filter(id => id !== addonId);
      }
      
      const hasChanges = JSON.stringify(newAddons.sort()) !== JSON.stringify(originalAddons.sort());
      setAddonChanges(hasChanges);
      
      return newAddons;
    });
  };

  const enabledAddons = getEnabledAddons();
  if (enabledAddons.length === 0) return null;

  // ✅ CHANGED: sort by category so add-ons of the same category sit next to each other,
  // but keep them all in ONE grid (instead of one small grid per category)
  const sortedAddons = [...enabledAddons].sort((a, b) =>
    (a.category || 'Other').localeCompare(b.category || 'Other')
  );

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-600 flex items-center justify-center">
            <Package size={16} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-medium">Rental Add-ons</h3>
            {/* ✅ CHANGED: "available • Selected • Cost" line removed */}
          </div>
        </div>
        {editIndex !== null && (
          <div className={`px-2 py-1 rounded-full text-xs ${
            addonChanges 
              ? 'bg-orange-900/30 text-orange-400 border border-orange-700' 
              : 'bg-green-900/30 text-green-400 border border-green-700'
          }`}>
            {addonChanges ? 'Changes' : 'No Changes'}
          </div>
        )}
      </div>

      {/* Addons Grid */}
      <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-4">
        {/* ✅ CHANGED: one grid, 2 columns per row on medium+ screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sortedAddons.map(addon => {
            const isSelected = selectedAddons.includes(addon.id);
            const addonCost = addon.pricingMethod === 'perDay' 
              ? `$${addon.price}/day ${calculationDetails.days > 0 ? `(Total: $${(parseFloat(addon.price) * calculationDetails.days).toFixed(2)})` : ''}`
              : `$${addon.price} (flat)`;

            return (
              <div
                key={addon.id}
                className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                  isSelected
                    ? 'border-blue-500 bg-blue-900/30'
                    : 'border-gray-600 bg-gray-700/50'
                } hover:border-blue-400`}
                onClick={() => handleAddonChange(addon.id, !isSelected)}
              >
                <div className="flex items-start gap-2">
                  {/* Checkbox */}
                  <div className={`mt-1 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSelected 
                      ? 'bg-blue-500 border-blue-500' 
                      : 'border-gray-500'
                  }`}>
                    {isSelected && <CheckCircle size={12} className="text-white" />}
                  </div>
                  
                  {/* Addon Info */}
                  <div className="min-w-0">
                    <div className="font-medium text-white text-sm">{addon.name}</div>
                    {/* ✅ CHANGED: category shown on the card instead of as a separate heading */}
                    <div className="text-xs text-blue-300/70 mt-0.5">{addon.category || 'Other'}</div>
                    {addon.description && (
                      <div className="text-xs text-gray-400 mt-0.5">{addon.description}</div>
                    )}
                    <div className="text-sm font-medium text-blue-400 mt-1">
                      {addonCost}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Total Cost Summary */}
        {selectedAddons.length > 0 && (
          <div className="mt-4 bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-300">Total Add-ons Cost:</span>
              <span className="text-blue-400 font-bold">${addonCosts.toFixed(2)}</span>
            </div>
            {calculationDetails.days > 0 && (
              <div className="text-xs text-blue-300 mt-1">
                Based on {calculationDetails.days} rental day{calculationDetails.days !== 1 ? 's' : ''}
              </div>
            )}
            {editIndex !== null && addonChanges && (
              <div className="mt-2 text-xs text-orange-400 bg-orange-900/20 px-2 py-1 rounded border border-orange-700">
                Changes will be saved when you update the reservation
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalAddonsSection;